import React, { useCallback } from 'react';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group';
import { cn } from '@/lib/utils';

const COMMON_PROTOCOLS = ['https://', 'http://', 'ftp://', 'ws://', 'wss://', 'file://'] as const;

/**
 * Removes protocol prefix from a URL
 * Checks both common protocols and the specific protocol prop
 */
const removeProtocol = (url: string, specificProtocol?: string): string => {
  if (!url) {
    return '';
  }

  // First check the specific protocol if provided
  if (specificProtocol && url.startsWith(specificProtocol)) {
    return url.slice(specificProtocol.length);
  }

  // Then check common protocols
  for (const protocol of COMMON_PROTOCOLS) {
    if (url.startsWith(protocol)) {
      return url.slice(protocol.length);
    }
  }

  // If no known protocol found, try to detect any protocol pattern (scheme://)
  const protocolMatch = url.match(/^[a-z][a-z0-9+.-]*:\/\//i);
  if (protocolMatch) {
    return url.slice(protocolMatch[0].length);
  }

  return url;
};

/**
 * Ensures URL has the specified protocol
 * @param url - The URL to process
 * @param protocol - The protocol to use (defaults to 'https://')
 */
const ensureProtocol = (url: string, protocol: string = 'https://'): string => {
  if (!url) {
    return '';
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }

  // Check if already has any protocol (using regex to catch all protocol schemes)
  const hasProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed);
  if (hasProtocol) {
    return trimmed;
  }

  // Add the specified protocol
  return `${protocol}${trimmed}`;
};

export interface URLInputProps extends Omit<
  React.ComponentProps<typeof InputGroupInput>,
  'value' | 'onChange'
> {
  value?: string;
  onChange?: (value: string) => void;
  protocol?: string;
}

/**
 * URLInput component with protocol addon prefix
 *
 * This is a fully controlled component that displays a URL input with a protocol
 * prefix addon (e.g., "https://"). The visual display removes the protocol for
 * cleaner UX, while the actual stored value always includes it.
 *
 * @example
 * ```tsx
 * // User sees: "example.com"
 * // Stored value: "https://example.com"
 * <URLInput
 *   value="https://example.com"
 *   onChange={(url) => console.log(url)}
 *   protocol="https://"
 * />
 * ```
 */
export const URLInput = ({
  value = '',
  onChange,
  protocol = 'https://',
  className,
  onBlur,
  ref,
  ...props
}: URLInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  // Calculate display value directly from prop (no internal state)
  const displayValue = removeProtocol(value, protocol);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Always store the full URL with protocol
      const fullUrl = inputValue ? ensureProtocol(inputValue, protocol) : '';
      onChange?.(fullUrl);
    },
    [onChange, protocol]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Trim whitespace on blur and update with full URL
      const trimmedValue = e.target.value.trim();
      const fullUrl = trimmedValue ? ensureProtocol(trimmedValue, protocol) : '';

      // Only call onChange if value actually changed
      const currentFullUrl = value ? ensureProtocol(value, protocol) : '';
      if (fullUrl !== currentFullUrl) {
        onChange?.(fullUrl);
      }

      // Call original onBlur if provided
      onBlur?.(e);
    },
    [onChange, protocol, value, onBlur]
  );

  // Extract aria-invalid and other accessibility props to forward to InputGroup
  const {
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedby,
    id,
    ...inputProps
  } = props;

  return (
    <InputGroup
      className={cn('overflow-hidden', className)}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedby}
      id={id}
    >
      <InputGroupAddon align="inline-start">
        <InputGroupText>{protocol}</InputGroupText>
      </InputGroupAddon>
      <InputGroupInput
        ref={ref}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        {...inputProps}
      />
    </InputGroup>
  );
};
